/**
 * Web Push subscription helpers.
 * Auto-registers service worker and subscribes on login (non-blocking).
 */
import { api } from './api';

function urlBase64ToUint8Array(b64) {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) out[i] = rawData.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return typeof navigator !== 'undefined' &&
         'serviceWorker' in navigator &&
         'PushManager' in window &&
         'Notification' in window;
}

export function isIOSSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const webkit = /WebKit/i.test(ua) && !/CriOS|FxiOS/.test(ua);
  return iOS && webkit;
}

export function isStandalone() {
  return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
         window.navigator.standalone === true;
}

let swRegPromise = null;

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  if (swRegPromise) return swRegPromise;
  swRegPromise = navigator.serviceWorker.register('/sw.js').catch((e) => {
    console.warn('[PUSH] SW register failed:', e);
    return null;
  });
  return swRegPromise;
}

export async function subscribeToPush({ silent = true } = {}) {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  if (isIOSSafari() && !isStandalone()) return { ok: false, reason: 'ios_not_installed' };

  try {
    const reg = await registerServiceWorker();
    if (!reg) return { ok: false, reason: 'no_sw' };

    let permission = Notification.permission;
    if (permission === 'default') {
      if (silent) {
        // Defer asking; parent page will call explicitly with silent=false
        return { ok: false, reason: 'permission_default' };
      }
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') return { ok: false, reason: 'denied' };

    // Get VAPID key
    const { publicKey } = await api.getVapidKey();
    const applicationServerKey = urlBase64ToUint8Array(publicKey);

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }
    const subJson = sub.toJSON();
    await api.subscribePush({
      endpoint: subJson.endpoint,
      keys: subJson.keys,
      user_agent: navigator.userAgent,
    });
    return { ok: true };
  } catch (err) {
    console.warn('[PUSH] subscribe failed:', err);
    return { ok: false, reason: err?.message || 'error' };
  }
}

export async function unsubscribeFromPush() {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return { ok: true };
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { ok: true };
    await api.unsubscribePush({ endpoint: sub.endpoint });
    await sub.unsubscribe();
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e?.message || 'error' };
  }
}
